import React, { FC, memo, useCallback, useState } from 'react'
import { HStack, Grid } from '@chakra-ui/react'
import { PhoneSpecs } from '../../phones'
import { PhoneListProps } from './phone-list'
import { IconButton } from '../icon-button'
import { CustomSortIcon } from '../icon'
import { Dropdown } from '../dropdown'
import i18next from 'i18next'
import { SortOption } from '../../state/interfaces'

export interface SortedPhoneListProps extends PhoneListProps {
  PhoneList: React.ComponentType<PhoneListProps>
}

const getSortOptionValue = (value: string) => Object.entries(SortOption).find(([key]) => key === value)?.[1]

const sortOptions = Object.entries(SortOption).reduce((accumulator, [value, label]) => {
  label = i18next.t(label)
  return [...accumulator, { label, value }]
}, [] as Array<{ label: string; value: string }>)

export const SortedPhoneList: FC<SortedPhoneListProps> = memo(({ PhoneList, phones, onClick }) => {
  const [sortBy, setSortBy] = useState<SortOption>(i18next.t(SortOption.POPULARITY))
  const [sortInAscendingOrder, setSortInAscendingOrder] = useState(true)
  const [refurbishedExcluded, setRefurbishedExcluded] = useState(false)

  const toggleSortOrder = useCallback(() => {
    setSortInAscendingOrder(!sortInAscendingOrder)
  }, [sortInAscendingOrder])

  const changeSortBy = (sortOption: SortOption) => {
    setSortBy(sortOption)
  }

  const handleRefurbished = () => {
    setRefurbishedExcluded(!refurbishedExcluded)
  }

  // useEffect(() => {
  //   if (refurbishedExcluded) {
  //     console.log('Excluding refurbished phones')
  //   }
  // }, [refurbishedExcluded])

  const sortPhones = useCallback(
    (phones: PhoneSpecs[]) => {
      let filteredPhones = phones
      if (refurbishedExcluded) {
        filteredPhones = filteredPhones.filter((phone) => phone.modelCondition !== 'refurbished')
      }

      const sortedPhones = [...filteredPhones].sort((a, b) => {
        switch (getSortOptionValue(sortBy)) {
          case SortOption.PHONE_PRICE:
            return b.modelPrice - a.modelPrice
          case SortOption.PHONE_NAME:
            return b.name.localeCompare(a.name)
          case SortOption.MEMORY_SIZE:
            return b.modelMemorySize - a.modelMemorySize
          default:
          case SortOption.POPULARITY:
            if (b.position === a.position) {
              return b.modelMemorySize - a.modelMemorySize
            }
            return b.position - a.position
        }
      })

      if (sortInAscendingOrder) {
        sortedPhones.reverse()
      }

      return sortedPhones
    },
    [sortBy, sortInAscendingOrder, refurbishedExcluded],
  )

  return (
    <>
      {phones.length > 0 && (
        <Grid templateColumns="repeat(auto-fill, 298px)" width="100%" justifyContent="center" paddingTop={4}>
          <HStack width="full" paddingLeft={2}>
            <HStack
              height={50}
              border={2}
              paddingLeft={2}
              paddingRight={2}
              borderStyle="solid"
              borderColor="grey"
              borderRadius="medium"
              justifyContent="center"
              backgroundColor="white"
              _hover={{
                borderColor: 'brand',
                boxShadow: '0 0 0 2px #5bce4c',
              }}
            >
              <IconButton
                label="sort"
                size="sm"
                icon={<CustomSortIcon color="green.500" height="16px" />}
                onClick={toggleSortOrder}
              />
            </HStack>

            <Dropdown
              options={sortOptions}
              value={sortBy}
              onChange={(value) => changeSortBy(value as SortOption)}
              excludeSelected={true}
            />
          </HStack>
          <HStack marginLeft={'auto'} height={50} alignItems="center">
            <label style={{ marginRight: '8px', fontSize: '18px' }}>{i18next.t('new-only')}</label>
            <div
              style={{
                position: 'relative',
                width: '40px',
                height: '20px',
                borderRadius: '10px',
                backgroundColor: refurbishedExcluded ? '#4caf50' : 'gray',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '2px',
                transition: 'background-color 0.3s',
              }}
              onClick={handleRefurbished}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  transform: refurbishedExcluded ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'transform 0.3s',
                }}
              ></div>
            </div>
          </HStack>
        </Grid>
      )}
      <PhoneList phones={sortPhones(phones)} onClick={onClick} />
    </>
  )
})
